import { useState } from "react";
import { Sparkles, Download } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { btnDark, btnLine, inputCls, useDownload } from "./shared";

export default function Letters({ caseId, items, letters, setLetters }) {
  const [active, setActive] = useState(items[0]?.template_key);
  const [busy, setBusy] = useState(false);
  const { download, busy: dl } = useDownload();
  if (!items.length) return <p className="text-sm text-muted">No letters are needed for your situation.</p>;
  const cur = letters[active] || {};
  const setText = (t) => setLetters({ ...letters, [active]: { ...cur, text: t } });

  const draft = async () => {
    if (cur.text && !window.confirm("Replace the current text with a new draft?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/letters/${active}/draft`);
      setLetters({ ...letters, [active]: data });
      toast.success(data.source === "ai" ? "Draft written from your details" : "Template filled from your details (AI unavailable)");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try { await api.put(`/cases/${caseId}/letters/${active}`, { text: cur.text || "" }); toast.success("Saved"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="letters-panel">
      <div className="mb-5 flex flex-wrap gap-x-5 gap-y-2 border-b border-navy/10 pb-3">
        {items.map((i) => (
          <button key={i.template_key} onClick={() => setActive(i.template_key)} data-testid={`letter-tab-${i.template_key}`}
            className={`text-sm ${active === i.template_key ? "border-b-2 border-navy font-semibold text-navy" : "text-muted hover:text-navy"}`}>
            {i.title}{letters[i.template_key]?.text ? "" : " (not written)"}
          </button>
        ))}
      </div>
      <p className="mb-3 text-sm text-muted">The draft uses only the facts you entered in Your details. Read it, correct anything untrue, and replace every [bracket]. You sign the printed letter yourself.</p>
      <textarea rows={18} className={`${inputCls} font-serif leading-relaxed`} value={cur.text || ""} onChange={(e) => setText(e.target.value)} data-testid="letter-text" placeholder="Nothing written yet. Use Draft from my details." />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={draft} disabled={busy} className={btnDark} data-testid="letter-draft"><Sparkles size={15} /> {busy ? "Writing…" : "Draft from my details"}</button>
        <button onClick={save} className={btnLine} data-testid="letter-save">Save edits</button>
        <button onClick={() => download(`/cases/${caseId}/letters/${active}/pdf`, `${active}.pdf`)} disabled={!cur.text || dl} className={btnLine} data-testid="letter-pdf"><Download size={14} /> PDF</button>
        {cur.source && <span className="text-xs text-muted">Source: {cur.source === "ai" ? "AI draft" : cur.source === "edited" ? "edited by you" : "template"}</span>}
      </div>
    </div>
  );
}
